import type { MessageDescriptor } from 'react-intl';

import { b } from 'utils/i18n';

type IntlMessage = MessageDescriptor & { values?: Record<string, unknown> };

export type InterventionUpdateModalTypes =
  | 'NEW_ADVICES'
  | 'ADVICE_VERSION'
  | 'UPDATE'
  | 'PAYMENT_SUCCESS'
  | 'PAYMENT_DEPOSIT_SUCCESS'
  | 'CHECKLIST_IMPORT'
  | 'SELF_SERVICE_PROACTIVES_ACCEPTED';

export const INTERVENTION_UPDATE_MODAL_TYPES: Record<InterventionUpdateModalTypes, string> = {
  NEW_ADVICES: 'NEW_ADVICES',
  ADVICE_VERSION: 'ADVICE_VERSION',
  UPDATE: 'UPDATE',
  PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
  PAYMENT_DEPOSIT_SUCCESS: 'PAYMENT_DEPOSIT_SUCCESS',
  CHECKLIST_IMPORT: 'CHECKLIST_IMPORT',
  SELF_SERVICE_PROACTIVES_ACCEPTED: 'SELF_SERVICE_PROACTIVES_ACCEPTED',
};

export type EventInfo = {
  title: IntlMessage;
  refreshButtonTitle: IntlMessage;
  description: IntlMessage;
};

export type EventProps = { isSalesOrParts?: boolean };

const INTERVENTION_PAYMENT_SUCCESS = (): Record<string, EventInfo> => ({
  default: {
    title: {
      id: 'web.update',
      defaultMessage: 'Update',
    },
    refreshButtonTitle: {
      id: 'web.refresh.data',
      defaultMessage: 'Refresh data',
    },
    description: {
      id: 'web.online-payment.modal.content',
      defaultMessage: 'Your payment status has been updated, please refresh data.',
    },
  },
});

const INTERVENTION_NEW_ADVICES = ({ isSalesOrParts }: EventProps): Record<string, EventInfo> => ({
  default: {
    title: {
      id: 'web.update',
      defaultMessage: 'Update',
    },
    refreshButtonTitle: {
      id: `web.new-advices.modal.goto.${isSalesOrParts ? 'sales' : 'aftersales'}`,
      defaultMessage: `Go to new ${isSalesOrParts ? 'items' : 'works'}`,
    },
    description: {
      id: `web.new-advices.modal.description.${isSalesOrParts ? 'sales' : 'aftersales'}`,
      defaultMessage: `<b>New ${isSalesOrParts ? 'items' : 'works'}</b> have been <b>added</b> to your ${
        isSalesOrParts ? 'offer' : 'work order'
      }`,
      values: { b },
    },
  },
});

const INTERVENTION_UPDATE = ({ isSalesOrParts }: EventProps): Record<string, EventInfo> => ({
  updatedFinalInvoice: {
    title: {
      id: 'web.update',
      defaultMessage: 'Update',
    },
    refreshButtonTitle: {
      id: 'web.finalInvoice.modal.button',
      defaultMessage: 'View changes',
    },
    description: {
      id: 'web.finalInvoice.modal.description',
      defaultMessage: 'Your invoice has been modified',
    },
  },
  default: {
    title: {
      id: 'web.warning',
      defaultMessage: 'Warning',
    },
    refreshButtonTitle: {
      id: 'web.refresh.data',
      defaultMessage: 'Refresh data',
    },
    description: {
      id: `web.advices.version.warning.${isSalesOrParts ? 'sales' : 'aftersales'}`,
      defaultMessage: `We have <b>updated</b> your <b>${
        isSalesOrParts ? 'offer' : 'work order'
      }</b>, please refresh data.`,
      values: { b },
    },
  },
});

export const EVENT_CONFIG = (props: EventProps): Record<string, Record<string, EventInfo>> => ({
  [INTERVENTION_UPDATE_MODAL_TYPES.PAYMENT_SUCCESS]: INTERVENTION_PAYMENT_SUCCESS(),
  [INTERVENTION_UPDATE_MODAL_TYPES.PAYMENT_DEPOSIT_SUCCESS]: INTERVENTION_PAYMENT_SUCCESS(),
  [INTERVENTION_UPDATE_MODAL_TYPES.NEW_ADVICES]: INTERVENTION_NEW_ADVICES(props),
  [INTERVENTION_UPDATE_MODAL_TYPES.UPDATE]: INTERVENTION_UPDATE(props),
  [INTERVENTION_UPDATE_MODAL_TYPES.CHECKLIST_IMPORT]: INTERVENTION_UPDATE(props),
  [INTERVENTION_UPDATE_MODAL_TYPES.ADVICE_VERSION]: INTERVENTION_UPDATE(props),
  [INTERVENTION_UPDATE_MODAL_TYPES.SELF_SERVICE_PROACTIVES_ACCEPTED]: INTERVENTION_UPDATE(props),
});
